// Reusable "Manage titles/outcomes" dialog. Lets the user add, rename or
// delete the strings used by the Title or Outcome dropdowns in the
// + Attempt form.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Pencil, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export default function ManageListDialog({
  open,
  onOpenChange,
  label,           // "titles" | "outcomes" — used in copy + testids
  listFn,
  createFn,
  renameFn,
  deleteFn,
  onChanged,       // parent refresh hook after any mutation
}) {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);   // { id, name }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const rows = await listFn();
      setItems(rows);
    } catch {
      toast.error(`Couldn't load ${label}`);
    }
  };

  useEffect(() => {
    if (open) {
      setNewName("");
      setEditing(null);
      load();
    }
  }, [open]);

  const add = async () => {
    const n = newName.trim();
    if (!n) return;
    setBusy(true);
    try {
      await createFn(n);
      setNewName("");
      await load();
      onChanged && onChanged();
      toast.success(`Added "${n}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const saveRename = async () => {
    const n = (editing?.name || "").trim();
    if (!editing || !n) return;
    setBusy(true);
    try {
      await renameFn(editing.id, n);
      setEditing(null);
      await load();
      onChanged && onChanged();
      toast.success("Renamed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rename");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    setBusy(true);
    try {
      await deleteFn(row.id);
      await load();
      onChanged && onChanged();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md p-0 gap-0"
        data-testid={`bd-manage-${label}-dialog`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E2DC]">
          <h3 className="text-base font-semibold text-[#1C2B26] capitalize">Manage {label}</h3>
        </div>

        <div className="p-5 space-y-3">
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={`Add a ${label.replace(/s$/, "")}…`}
              onKeyDown={(e) => e.key === "Enter" && add()}
              data-testid={`bd-manage-${label}-new-input`}
            />
            <Button
              onClick={add}
              disabled={busy || !newName.trim()}
              data-testid={`bd-manage-${label}-add-btn`}
              className="bg-[#2E7D5B] hover:bg-[#25664A]"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="divide-y divide-[#E5E2DC] max-h-[320px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="text-sm text-[#6B7A73] py-3">
                No {label} yet. Add one above.
              </div>
            ) : items.map((row) => (
              <div
                key={row.id}
                className="flex items-center gap-2 py-2"
                data-testid={`bd-manage-${label}-row-${row.id}`}
              >
                {editing?.id === row.id ? (
                  <>
                    <Input
                      value={editing.name}
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && saveRename()}
                      autoFocus
                      data-testid={`bd-manage-${label}-edit-input`}
                    />
                    <Button
                      size="sm"
                      onClick={saveRename}
                      disabled={busy}
                      className="bg-[#2E7D5B] hover:bg-[#25664A]"
                      data-testid={`bd-manage-${label}-save-btn`}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing(null)}
                      data-testid={`bd-manage-${label}-cancel-btn`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 text-sm text-[#1C2B26]">{row.name}</div>
                    <button
                      type="button"
                      onClick={() => setEditing({ id: row.id, name: row.name })}
                      className="p-2 rounded hover:bg-[#F4F3EF] text-[#6B7A73]"
                      data-testid={`bd-manage-${label}-rename-${row.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(row)}
                      className="p-2 rounded hover:bg-[#F5DAD3] text-[#B4351B]"
                      data-testid={`bd-manage-${label}-delete-${row.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
